import React, { useRef, useState } from "react";
import { toast } from "sonner";
import { api, openSignedFile } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { FileMinus, Upload, Eye, Loader2 } from "lucide-react";
import { prepareDoc } from "@/components/InvoiceSection";

const inr = (n) => `₹${(n || 0).toFixed(2)}`;

export function CreditNoteSection({ order, isSupplier, canUpload, onChanged }) {
  const [busy, setBusy] = useState(false);
  const [note, setNote] = useState("");
  const ref = useRef(null);
  const cn = order.credit_note;
  const showUpload = isSupplier && canUpload && order.status === "Missed Delivery";
  if (!cn && !showUpload) return null;
  const shortfall = Math.max(0, (order.order_total || 0) - (order.billed_total || 0));

  const upload = async (raw) => {
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", await prepareDoc(raw, "credit notes"));
      fd.append("note", note);
      await api.post(`/orders/${order.id}/credit-note`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(cn ? "Credit note replaced" : "Credit note attached", { description: "The buyer has been notified." });
      setNote(""); onChanged();
    } catch (e) { toast.error(e.response?.data?.detail || e.message || "Upload failed"); } finally { setBusy(false); }
  };
  const view = () => openSignedFile(cn.file_id).catch((e) => toast.error(e.response?.data?.detail || "Could not open credit note"));

  return (
    <div className="rounded-2xl border border-rose-100 bg-rose-50/50 p-4" data-testid="order-credit-note-section">
      <p className="mb-2 text-xs font-semibold uppercase tracking-wider text-rose-400">Credit note</p>
      {cn ? (
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="flex min-w-0 items-center gap-3">
            <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-white text-rose-600"><FileMinus className="h-5 w-5" /></div>
            <div className="min-w-0">
              <p className="truncate text-sm font-semibold text-stone-800" data-testid="order-credit-note-filename">{cn.filename}</p>
              <p className="text-xs text-stone-500"><span className="font-semibold text-rose-700" data-testid="order-credit-note-amount">{inr(cn.amount)}</span> credited · {new Date(cn.uploaded_at).toLocaleString("en-IN")}{cn.uploaded_by ? ` by ${cn.uploaded_by}` : ""}</p>
              {cn.note && <p className="mt-1 text-xs text-stone-600" data-testid="order-credit-note-note">{cn.note}</p>}
            </div>
          </div>
          <Button size="sm" variant="outline" onClick={view} data-testid="order-credit-note-view-btn"><Eye className="mr-1 h-4 w-4" /> View / Download</Button>
        </div>
      ) : (
        <p className="text-sm text-stone-600">Attach a credit note for the <b>{inr(shortfall)}</b> not delivered. The buyer will be notified.</p>
      )}
      {showUpload && (
        <div className="mt-3 flex flex-wrap items-center gap-2">
          <Input value={note} onChange={(e) => setNote(e.target.value)} maxLength={300} placeholder="Note (optional), e.g. CN-104" className="h-9 max-w-xs bg-white" data-testid="order-credit-note-note-input" />
          <input ref={ref} type="file" accept=".pdf,.jpg,.jpeg,.png,application/pdf,image/jpeg,image/png" className="hidden" data-testid="order-credit-note-file-input"
            onChange={(e) => { const f = e.target.files?.[0]; e.target.value = ""; if (f) upload(f); }} />
          <Button size="sm" disabled={busy} onClick={() => ref.current?.click()} className="bg-rose-600 hover:bg-rose-700" data-testid="order-credit-note-upload-btn">
            {busy ? <Loader2 className="mr-1 h-4 w-4 animate-spin" /> : <Upload className="mr-1 h-4 w-4" />} {cn ? "Replace Credit Note" : "Attach Credit Note"}
          </Button>
          <p className="w-full text-xs text-stone-400">PDF, JPEG or PNG · max 2 MB</p>
        </div>
      )}
    </div>
  );
}
